import { useMutation, type QueryClient } from '@tanstack/react-query';

import { type AuditService } from '#apps/client-web/features/audit/services/audit';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type FileStorageService } from '#apps/client-web/features/file-storage/services/file-storage';
import { type UploadApiService } from '#apps/client-web/features/upload/services/upload-api';
import type { UpdateUploadStateAction } from '#apps/client-web/pages/audit-review-submit/audit-review-submit-page.view-model';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  AuditSchema,
  type Audit,
  type AuditId,
} from '#packages/data-collection/models/audit';
import { type AuditFormType } from '#packages/data-collection/models/audit-form';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form/audit-background-form';
import { type AuditLightingTypeForm } from '#packages/data-collection/models/audit-lighting-type-form';
import { type AuditMechanicalForm } from '#packages/data-collection/models/audit-mechanical-form';
import { type AuditWaterFixtureForm } from '#packages/data-collection/models/audit-water-fixture-form';
import { type AuditWindowsForm } from '#packages/data-collection/models/audit-windows-form';
import { type Attachment } from '#packages/data-collection/models/common/attachment';

import { UPLOAD_AUDIT_QUERY_KEY } from '../types/react-query';

const ALL_FORM_TYPES: AuditFormType[] = [
  'background',
  'mechanical',
  'lighting-type',
  'water-fixture',
  'windows',
];

export type FormLoadState = 'idle' | 'loading' | 'success' | 'error';
export type FormUploadState = 'idle' | 'pending' | 'success' | 'error';

type AuditFormTypesWithAttachments = Extract<
  AuditFormType,
  'background' | 'mechanical' | 'lighting-type' | 'water-fixture' | 'windows'
>;

export type AuditUploadState = {
  preparing: FormLoadState;
  forms: FormUploadState;
  attachments: Record<AuditFormTypesWithAttachments, FormUploadState>;
};

const extractBackgroundFormAttachments = (
  form: AuditBackgroundForm,
): Attachment[] => {
  const attachments: Attachment[] = [];
  for (const meter of form.general?.meters ?? []) {
    attachments.push(...(meter.attachments ?? []));
  }
  attachments.push(...(form.controls?.attachments ?? []));
  attachments.push(...(form.envelope?.coverPhoto ?? []));
  for (const entry of form.envelope?.windowTypes ?? []) {
    attachments.push(...(entry.attachments ?? []));
  }
  for (const entry of form.envelope?.doorTypes ?? []) {
    attachments.push(...(entry.attachments ?? []));
  }
  for (const entry of form.envelope?.wallTypes ?? []) {
    attachments.push(...(entry.attachments ?? []));
  }
  for (const entry of form.envelope?.roofingTypes ?? []) {
    attachments.push(...(entry.attachments ?? []));
  }
  attachments.push(...(form.buildingAndSystems?.attachments ?? []));
  return attachments;
};

const uploadAttachments = async ({
  attachments,
  serverAuditId,
  auditId,
  accessToken,
  uploadApiService,
  fileStorageService,
  logger,
}: {
  attachments: Attachment[];
  serverAuditId: string;
  auditId: string;
  accessToken: string | undefined;
  uploadApiService: UploadApiService;
  fileStorageService: FileStorageService;
} & LoggingContext) => {
  for (const attachment of attachments) {
    const fileId = attachment.name;
    const fileContent = await fileStorageService.load({ fileId });
    if (!fileContent) {
      logger?.warn(
        { auditId, fileId },
        'File not found in local storage, skipping upload',
      );
      continue;
    }
    const fileExists = await uploadApiService.checkFileExists({
      serverAuditId,
      fileId,
      accessToken,
    });
    if (fileExists) {
      logger?.debug(
        { auditId, serverAuditId, fileId },
        'File already exists, skipping upload',
      );
      continue;
    }
    logger?.debug(
      { auditId, serverAuditId, fileId },
      'Getting file upload URL',
    );
    const uploadUrl = await uploadApiService.getFileUploadUrl({
      serverAuditId,
      fileId,
      accessToken,
      replace: true,
    });
    await uploadApiService.uploadFileToUrl({
      url: uploadUrl,
      content: fileContent,
      mimeType: attachment.mimeType,
    });
    logger?.debug(
      { auditId, serverAuditId, fileId },
      'File uploaded successfully',
    );
  }
};

export const useUploadAuditMutation = ({
  auditId,
  queryClient,
  auditService,
  auditFormService,
  fileStorageService,
  uploadApiService,
  getAccessToken,
  logger,
  updateState,
}: {
  auditId: AuditId;
  queryClient: QueryClient;
  auditService: AuditService;
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  uploadApiService: UploadApiService;
  getAccessToken: () => string | undefined;
  updateState: (action: UpdateUploadStateAction) => void;
} & LoggingContext) =>
  useMutation(
    {
      mutationKey: [UPLOAD_AUDIT_QUERY_KEY, auditId],
      mutationFn: async (audit: Audit) => {
        const auditId = audit.id;
        const accessToken = getAccessToken();

        // 1. Load all forms from local storage
        updateState({ type: 'update', payload: { preparing: 'loading' } });
        logger?.debug({ auditId }, 'Loading audit forms for upload');
        const formResults = await Promise.all(
          ALL_FORM_TYPES.map((formType) =>
            auditFormService.loadByType({ auditId, formType }),
          ),
        ).catch((err) => {
          updateState({ type: 'update', payload: { preparing: 'error' } });
          throw err;
        });
        const forms = (formResults ?? []).filter(
          (f): f is NonNullable<typeof f> => f !== null,
        );
        logger?.debug(
          { auditId, formCount: forms.length },
          'Loaded forms for upload',
        );

        // 2. Upload audit + forms to server → get server UUID
        const serializedAudit = AuditSchema.encode(audit);
        logger?.debug({ auditId }, 'Uploading audit to server');
        updateState({ type: 'update', payload: { preparing: 'success' } });
        updateState({ type: 'update_forms', payload: 'pending' });

        const uploadResult = await uploadApiService
          .uploadAudit({
            audit: serializedAudit,
            forms,
            accessToken,
          })
          .catch((err) => {
            updateState({ type: 'update_forms', payload: 'error' });
            throw err;
          });

        logger?.debug(
          { auditId, serverAuditId: uploadResult.id },
          'Audit uploaded successfully',
        );
        updateState({ type: 'update_forms', payload: 'success' });

        // 3. Upload attachment files using the server UUID
        const attachmentsByFormType: Record<
          AuditFormTypesWithAttachments,
          Attachment[]
        > = {
          background: extractBackgroundFormAttachments(
            (forms.find(
              (f) => f.type === 'background',
            ) as AuditBackgroundForm) ?? {},
          ),
          mechanical:
            (
              forms.find((f) => f.type === 'mechanical') as
                | AuditMechanicalForm
                | undefined
            )?.entries.flatMap((e) => e.attachments ?? []) ?? [],
          'lighting-type':
            (
              forms.find((f) => f.type === 'lighting-type') as
                | AuditLightingTypeForm
                | undefined
            )?.entries.flatMap((e) => e.attachments ?? []) ?? [],
          'water-fixture':
            (
              forms.find((f) => f.type === 'water-fixture') as
                | AuditWaterFixtureForm
                | undefined
            )?.entries.flatMap((e) => e.attachments ?? []) ?? [],
          windows:
            (
              forms.find((f) => f.type === 'windows') as
                | AuditWindowsForm
                | undefined
            )?.entries.flatMap((e) => e.attachments ?? []) ?? [],
        };

        for (const [formType, attachments] of Object.entries(
          attachmentsByFormType,
        ) as [AuditFormTypesWithAttachments, Attachment[]][]) {
          updateState({
            type: 'update_attachments',
            payload: { [formType]: 'pending' },
          });
          await uploadAttachments({
            attachments,
            serverAuditId: uploadResult.id,
            auditId,
            accessToken,
            uploadApiService,
            fileStorageService,
            logger,
          }).catch((err) => {
            updateState({
              type: 'update_attachments',
              payload: { [formType]: 'error' },
            });
            throw err;
          });
          updateState({
            type: 'update_attachments',
            payload: { [formType]: 'success' },
          });
        }

        // 4. Persist server-assigned UUID locally
        logger?.debug(
          { auditId, serverAuditId: uploadResult.id },
          'Saving serverAuditId to local audit',
        );
        await auditService.update({
          collectionName: 'active',
          updatedAudit: { ...audit, serverAuditId: uploadResult.id },
        });

        return uploadResult;
      },
    },
    queryClient,
  );
